import React, { useState, useEffect } from "react";
import Icon from "../common/Icon";
import Avatar from "../common/Avatar";
import { dailyReflection, weeklyHighlights } from "../../data/spiritualPlan";
import { suggestedPilgrims, challengeData, announcement } from "../../data/users";
import { Anoucement, ChallangeBG, Comments, Posts, SolarFire } from "../../assets/images/image";
import { Flame, Loader2, Menu, X } from "lucide-react";
import api from "../../services/api";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAssetUrl } from "../../services/helper";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
const DailyReflectionCard = () => (
  <div className="bg-primary rounded-card p-5 text-white relative overflow-hidden">
    <div
      className="absolute top-2 right-4 text-[64px] leading-none opacity-20 select-none"
      style={{ fontFamily: "Georgia, serif", color: "#D4A843" }}
    >
      99
    </div>
    <p className="flex items-center gap-1.5 text-[11px] font-bold tracking-[2px] uppercase mb-3 text-gold">
      <Icon name="sparkle" size={14} className="text-gold" /> DAILY REFLECTION
    </p>
    <p className="text-[15px] font-bold leading-[1.4] mb-3 font-body">
      {dailyReflection.quote}
    </p>
    <p className="text-[12px] text-gray-300 leading-relaxed mb-4">
      {dailyReflection.prompt}
    </p>
    <button className="w-full py-2.5 bg-gold text-primary rounded-full font-bold text-[13px] hover:brightness-110 transition-all">
      {dailyReflection.ctaLabel}
    </button>
  </div>
);

const WeeklyHighlightsCard = () => (
  <div className="bg-white rounded-card border border-gray-100 p-5">
    <p className="flex items-center gap-1.5 text-[11px] font-bold tracking-[2px] uppercase text-gray-800 mb-4">
      ⊕ WEEKLY HIGHLIGHTS
    </p>
    <div className="flex flex-col gap-3.5">
      {weeklyHighlights.map((item) => (
        <div key={item.id} className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center flex-shrink-0 text-white">
            <Icon name={item.icon} size={18} />
          </div>
          <div>
            <p className="text-[13px] font-bold text-gray-800">{item.title}</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {item.description}
            </p>
          </div>
        </div>
      ))}
    </div>
    <p className="text-[13px] text-primary font-semibold mt-4 text-center cursor-pointer hover:underline">
      View all updates
    </p>
  </div>
);

const SuggestedPilgrimsCard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [followingId, setFollowingId] = useState(null);
  const { user, logout } = useAuth();


  // Fetch suggested pilgrims
  const { data, isLoading, error } = useQuery({
    queryKey: ['suggested-pilgrims'],
    queryFn: () => api.fetchSuggestedPilgrim(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const suggestedPilgrims = data?.data || [];

  // Follow mutation
  const followMutation = useMutation({
    mutationFn: (userId) => api.followRequest(userId),
    onSuccess: (userId) => {
      setFollowingId(userId);

      toast.success("Follow Request Submitted")
      // Optimistic update or invalidate
      queryClient.invalidateQueries({ queryKey: ['suggested-pilgrims'] });
      // You could also update local state for immediate UI feedback
    },
    onError: (err) => {
      console.error('Follow failed:', err);
      // Show toast notification here if you have one
    },
    onSettled: () => {
      setFollowingId(null);
    },
  });

  const handleFollow = (userId) => {
    followMutation.mutate(userId);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-card border border-gray-100 p-4 w-[384px]">
        <p className="text-[12px] font-medium font-body tracking-[2px] uppercase text-[#0F1D2E] mb-3">
          SUGGESTED PILGRIMS
        </p>
        <div className="flex flex-col gap-2.5">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-2 animate-pulse">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-gray-200 rounded-full" />
                <div className="space-y-1">
                  <div className="h-3.5 w-28 bg-gray-200 rounded" />
                  <div className="h-3 w-16 bg-gray-200 rounded" />
                </div>
              </div>
              <div className="h-7 w-16 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-card border border-gray-100 p-4 w-[384px]">
        <p className="text-[12px] font-medium font-body tracking-[2px] uppercase text-[#0F1D2E] mb-3">
          SUGGESTED PILGRIMS
        </p>
        <p className="text-red-500 text-sm">Failed to load suggestions</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-card border border-gray-100 p-4 w-[384px]">
      <p className="text-[12px] font-medium font-body tracking-[2px] uppercase text-[#0F1D2E] mb-3">
        SUGGESTED PILGRIMS
      </p>
      <div className="flex flex-col gap-2.5">
        {suggestedPilgrims.map((pilgrim) => (
          <div key={pilgrim.id} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => navigate(`/other-profile/${pilgrim.id}`)}>
              <Avatar
                src={getAssetUrl(pilgrim.avatar)}
                alt={pilgrim.name}
                size={38}
                className="flex-shrink-0"
              />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold font-body text-[#14171A] truncate">
                  {pilgrim.name}
                </p>
                <p className="text-[11px] font-body text-[#657786] capitalize">
                  suggested
                </p>
              </div>
            </div>
            <button
              onClick={() => handleFollow(pilgrim.id)}
              disabled={followingId === pilgrim.id || pilgrim.is_connected==1 || (user && user.role === "free")}
              className="px-3 py-1.5 bg-[#1F3A5F] font-body text-white rounded-[35.74px] text-[12px] hover:bg-primary-dark transition-colors flex-shrink-0 disabled:opacity-70"
            >
              {pilgrim.is_connected==1 ? 'Already Connected': followingId === pilgrim.id ? "Following..." : "Follow"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChallengeCard = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["current-challenge"],
    queryFn: () => api.getCurrentChallenge(), // Adjust API method name if needed
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });

  const challenge = data?.data;
  const message = data?.message;


  // Loading State
  if (isLoading) {
    return (
      <div className="w-[384px] h-[420px] rounded-[15.28px] bg-[#1F3A60] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-white">
          <Loader2 size={28} className="animate-spin" />
          <p className="text-sm">Loading challenge...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (isError || !challenge) {
    return (
      <div className="w-[375px] rounded-[15.28px] bg-[#1F3A60] p-6 text-white text-center">
        {/* <p className="text-red-300 mb-2">Failed to load challenge</p> */}
        <p className="text-sm opacity-75">{error?.message || message}</p>
      </div>
    );
  }

  const postsPercent = Math.round(challenge.posts_progress) || 0;
  const commentsPercent = Math.round(challenge.comments_progress) || 0;
  const totalPercent = Math.round(challenge.total_progress) || 0;

  return (
    <div
      className="w-[375px] rounded-[15.28px] p-5 text-white bg-cover bg-center bg-no-repeat relative overflow-hidden"
      style={{
        backgroundImage: `url(${ChallangeBG})`,
      }}
    >
      {/* Header */}
      <div className="mb-4">
        <p className="text-[13px] font-semibold text-white">Challenge</p>

        <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center">
              <img src={SolarFire} alt="fire" className="w-6 h-6" />
            </div>
            <p className="text-[13px] font-medium text-white/90">
              Day <span className="font-bold">{challenge.current_day}</span> of{" "}
              <span className="font-bold">{challenge.duration_days}</span>
            </p>
          </div>

          <div className="bg-white text-[#4b4b4b] px-4 py-1.5 rounded-full text-[12px] font-bold shadow-sm whitespace-nowrap">
            Reward: <span className="text-[#1F3A60]">+{challenge.reward_points} points</span>
          </div>
        </div>
      </div>

      {/* Title */}
      <h2 className="bricolage-font text-[22px] leading-[32px] font-extrabold mb-3">
        {challenge.title}
      </h2>

      <p className="text-[13px] leading-[20px] text-white mb-4 opacity-90">
        Choose how you want to participate. You can change this anytime.
      </p>

      {/* Posts Progress */}
      <div className="bg-white/[0.08] rounded-xl p-2.5 mb-3 flex items-center gap-3 backdrop-blur-sm">
        <div className="w-[35px] h-[35px] rounded-full bg-[#1F3A60] flex items-center justify-center flex-shrink-0">
          <img src={Posts} alt="posts" className="w-4 h-4" />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[13px] font-semibold text-white">Posts</p>
            <p className="text-[12px] text-white/80 font-medium">
              {challenge.completed_posts}/{challenge.required_posts}
            </p>
          </div>
          <div className="w-full h-[6px] bg-white/15 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-[#1F3A60] transition-all"
              style={{ width: `${postsPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Comments Progress */}
      <div className="bg-white/[0.08] rounded-xl p-2.5 mb-4 flex items-center gap-3 backdrop-blur-sm">
        <div className="w-[35px] h-[35px] rounded-full bg-[#1F3A60] flex items-center justify-center flex-shrink-0">
          <img src={Comments} alt="comments" className="w-4 h-4" />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[13px] font-semibold text-white">Comments</p>
            <p className="text-[12px] text-white/80 font-medium">
              {challenge.completed_comments}/{challenge.required_comments}
            </p>
          </div>
          <div className="w-full h-[6px] bg-white/15 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-[#1F3A60] transition-all"
              style={{ width: `${commentsPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Total Progress */}
      <div className="mb-1">
        <p className="text-[13px] font-semibold text-white mb-2">Total Progress</p>
        <div className="w-full h-[10px] rounded-full bg-white/15 overflow-hidden mb-2">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${totalPercent}%`,
              background: "linear-gradient(90deg, #D4A843 0%, #F4D06F 100%)",
            }}
          />
        </div>
        <p className="text-[13px] font-semibold text-[#F4D06F]">
          {totalPercent}% complete
        </p>
      </div>

      {/* Status Badge */}
      {challenge.status && (
        <div className="absolute top-5 right-5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
          {challenge.status.toUpperCase()}
        </div>
      )}
    </div>
  );
};

const AnnouncementsCard = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["custom-announcements"],
    queryFn: () => api.getAnnouncements(), // Adjust API method name if needed
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    retry: 2,
  });

  const announcement = data?.data?.custom_announcement; // Assuming API returns { success, data: { title?, content, ... } }

  if (isLoading) {
    return (
      <div className="bg-white rounded-[12px] border border-gray-100 p-6 w-[384px] flex items-center justify-center h-[180px]">
        <div className="flex flex-col items-center gap-2 text-[#6B7280]">
          <Loader2 size={24} className="animate-spin" />
          <p className="text-sm">Loading announcements...</p>
        </div>
      </div>
    );
  }

  if (isError || !announcement) {
    return (
      <div className="bg-white rounded-[12px] border border-gray-100 p-6 w-[384px]">
        <div className="flex items-center gap-2 mb-3">
          <img src={Anoucement} alt="Announcement" className="object-contain" />
          <p className="text-[15px] font-medium text-[#0F1D2E] leading-none">
            Announcements
          </p>
        </div>
        <p className="text-[14px] text-red-500">
          Failed to load announcements. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] border border-gray-100 p-4 w-[384px]">
      <div className="flex items-center gap-2 mb-3">
        <img
          src={Anoucement}
          alt="Announcement"
          className="object-contain"
        />
        <p className="text-[15px] font-medium text-[#0F1D2E] leading-none">
          Announcements
        </p>
      </div>

      <p className="text-[14px] font-normal leading-[22px] text-[#6B7280]">
        {announcement ||
          "No announcement available at the moment."}
      </p>


    </div>
  );
};

const RightSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1536);

  // Auto-open at 1536px (xl-2)
  useEffect(() => {
    const handleResize = () => {
      const large = window.innerWidth >= 1536;
      setIsLargeScreen(large);
      if (large) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Hamburger Button - visible when sidebar is closed on small screens */}
      {!isOpen && !isLargeScreen && (
        <button
          onClick={toggleSidebar}
          className="my-6 mr-6 right-4 z-50 p-2.5 bg-primary rounded-lg text-white hover:bg-primary-dark transition-all duration-300 shadow-lg"
        >
          <Menu size={24} />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`
          h-full
          overflow-y-auto
          scrollbar-hide
          flex-shrink-0
          flex
          flex-col
          gap-4
          pt-20
          2xl:pt-6
          pb-6
          w-[380px]
          transition-all
          duration-300
          ease-in-out
          fixed
          top-0
          right-0
          z-40
          bg-white
          shadow-2xl
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          ${isLargeScreen ? '2xl:overflow-hidden 2xl:relative 2xl:translate-x-0 2xl:shadow-none 2xl:bg-transparent 2xl:mr-6 2xl:top-auto 2xl:right-auto 2xl:z-auto' : ''}
        `}
        style={{
          width: isLargeScreen ? '380px' : '420px'
        }}
      >
        {/* Close button - only visible when sidebar is open and screen is smaller than 1536px */}
        {isOpen && !isLargeScreen && (
          <button
            onClick={toggleSidebar}
            className="absolute top-4 right-4 z-50 p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        )}

        <div className="flex flex-col gap-4 pb-10 px-4 2xl:px-0">
          <SuggestedPilgrimsCard />
          <ChallengeCard />
          <AnnouncementsCard />
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && !isLargeScreen && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default RightSidebar;